// 포인터 입력: 한 손가락 탭 → onTap, 드래그 → 카메라 회전, 두 손가락 핀치·마우스 휠 → 줌.
// 캔버스에는 CSS touch-action: none 이 걸려 있어야 브라우저 스크롤·확대와 겹치지 않는다.
import * as THREE from 'three';
import type { FollowCamera } from './camera';

/** 이 거리(px) 넘게 움직이면 탭이 아니라 드래그. */
const TAP_SLOP = 6;
/** 이 시간(ms) 안에 떼야 탭. */
const TAP_MS = 500;

export interface InputHandlers {
  onTap(clientX: number, clientY: number): void;
  /** 한 손가락으로 누른 곳이 밀기 대상이면 true (그 드래그는 카메라를 돌리지 않고 onSwipe로). */
  swipeStart?(clientX: number, clientY: number): boolean;
  /** 밀기가 끝났을 때 이동한 양(px). */
  onSwipe?(dx: number, dy: number): void;
}

export function attachInput(canvas: HTMLCanvasElement, cam: FollowCamera, h: InputHandlers): () => void {
  const pointers = new Map<number, { x: number; y: number }>();
  let down: { x: number; y: number; t: number; moved: boolean; swipe: boolean } | null = null;
  let pinch: { d: number; dist: number } | null = null;
  const pinchDist = () => {
    const [p, q] = [...pointers.values()];
    return Math.hypot(p!.x - q!.x, p!.y - q!.y) || 1;
  };

  const onDown = (e: PointerEvent) => {
    canvas.setPointerCapture(e.pointerId);
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 1) down = { x: e.clientX, y: e.clientY, t: performance.now(), moved: false, swipe: !!h.swipeStart?.(e.clientX, e.clientY) };
    else { down = null; pinch = { d: pinchDist(), dist: cam.dist }; }
  };
  const onMove = (e: PointerEvent) => {
    const p = pointers.get(e.pointerId);
    if (!p) return;
    const dx = e.clientX - p.x, dy = e.clientY - p.y;
    p.x = e.clientX; p.y = e.clientY;
    if (pointers.size === 1) {
      if (down && Math.hypot(e.clientX - down.x, e.clientY - down.y) > TAP_SLOP) down.moved = true;
      if ((!down || down.moved) && !down?.swipe) cam.orbit(dx, dy);
    } else if (pinch) cam.zoomTo(pinch.dist * pinch.d / pinchDist());
  };
  const onUp = (e: PointerEvent) => {
    if (down && !down.moved && pointers.size === 1 && performance.now() - down.t < TAP_MS) h.onTap(e.clientX, e.clientY);
    else if (down && down.swipe && down.moved && pointers.size === 1) h.onSwipe?.(e.clientX - down.x, e.clientY - down.y);
    pointers.delete(e.pointerId);
    if (pointers.size < 2) pinch = null;
    if (!pointers.size) down = null;
  };
  const onCancel = (e: PointerEvent) => { pointers.delete(e.pointerId); pinch = null; down = null; };
  const onWheel = (e: WheelEvent) => { e.preventDefault(); cam.zoomTo(cam.dist * (1 + e.deltaY * 0.001)); };

  canvas.addEventListener('pointerdown', onDown);
  canvas.addEventListener('pointermove', onMove);
  canvas.addEventListener('pointerup', onUp);
  canvas.addEventListener('pointercancel', onCancel);
  canvas.addEventListener('wheel', onWheel, { passive: false });
  return () => {
    canvas.removeEventListener('pointerdown', onDown);
    canvas.removeEventListener('pointermove', onMove);
    canvas.removeEventListener('pointerup', onUp);
    canvas.removeEventListener('pointercancel', onCancel);
    canvas.removeEventListener('wheel', onWheel);
  };
}

const ray = new THREE.Raycaster(), ndc = new THREE.Vector2();

/** 화면 좌표에서 나가는 광선. 캐릭터·물건 고르기에 쓴다. */
export function rayFrom(canvas: HTMLCanvasElement, camera: THREE.Camera, clientX: number, clientY: number): THREE.Raycaster {
  const r = canvas.getBoundingClientRect();
  ndc.set((clientX - r.left) / r.width * 2 - 1, -(clientY - r.top) / r.height * 2 + 1);
  ray.setFromCamera(ndc, camera);
  return ray;
}

const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

/** 광선이 높이 y인 바닥과 만나는 점. 없으면 null. */
export function hitGround(r: THREE.Raycaster, out: THREE.Vector3, y = 0): THREE.Vector3 | null {
  plane.constant = -y;
  return r.ray.intersectPlane(plane, out);
}
